import type { BargeinOutcome } from '@voicer/shared';

const BACKCHANNELS = new Set([
  // English
  'yes', 'yeah', 'yep', 'yup', 'ya', 'ok', 'okay', 'k', 'right', 'sure', 'mhm', 'mm', 'mmm', 'mhmm', 'mm-hmm', 'mmhmm', 'uh-huh', 'uhhuh', 'uh huh',
  'i see', 'got it', 'gotcha', 'go on', 'go ahead', 'keep going', 'interesting', 'cool', 'nice', 'wow', 'hmm', 'hm', 'huh', 'aha', 'ah', 'oh', 'alright', 'all right',
  'true', 'exactly', 'totally', 'indeed', 'makes sense', 'that makes sense', 'fair', 'fair enough', 'okay okay', 'yes yes', 'i know', 'i understand', 'understood',
  'good', 'great', 'perfect', 'awesome', 'thanks', 'thank you', 'please continue', 'continue', 'and', 'so', 'um', 'uh', 'er', 'like', 'well',
  // Spanish / French / German / Portuguese / Italian (common backchannels)
  'si', 'sí', 'vale', 'claro', 'aja', 'ajá', 'bueno', 'entiendo',
  'oui', 'ouais', 'daccord', "d'accord", 'bien', 'ouais ouais',
  'ja', 'genau', 'stimmt', 'aha', 'ach so', 'verstehe',
  'sim', 'isso', 'entendi', 'tá', 'ta',
  'si si', 'capito', 'esatto', 'va bene',
]);

const INTERRUPT_WORDS =
  /\b(wait|stop|hold on|hang on|no no|actually|hey|excuse me|shut up|enough|pause|quiet|but|however|one second|one sec|sorry|espera|para|arrêt|warte|para aí|ferma)\b/i;
const QUESTION = /\b(what|why|how|when|where|who|which|can you|could you|would you|do you|did you|is it|are you)\b|\?$/i;
const NEGATION = /\b(no|not|don't|dont|nope|never|wrong|incorrect|disagree)\b/i;

// Whisper hallucinations on near-silence or noise.
const HALLUCINATIONS = [
  /^thank you\.?$/i, /^thanks for watching\.?$/i, /^you$/i, /^bye\.?$/i, /^\.+$/, /^(the end|subtitles? by|amara\.org).*/i, /^so\.?$/i, /^\W*$/,
];

export function normalize(t: string): string {
  return t
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9'\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface LexicalResult {
  outcome: BargeinOutcome;
  confident: boolean;
  reason: string;
}

/**
 * Tier A: zero-latency lexical classification of a short interjection.
 * Confident outcomes are applied immediately; unconfident ones go to the in-context classifier.
 */
export function classifyLexical(transcript: string, assistantAskedQuestion: boolean): LexicalResult {
  const raw = transcript.trim();
  if (!raw || HALLUCINATIONS.some((re) => re.test(raw))) return { outcome: 'NOISE', confident: true, reason: 'empty or Whisper hallucination pattern' };
  const t = normalize(raw);
  const words = t.split(' ').filter(Boolean);

  if (INTERRUPT_WORDS.test(t)) return { outcome: 'INTERRUPT', confident: true, reason: 'interrupt keyword' };
  if (QUESTION.test(raw) && words.length >= 2) return { outcome: 'INTERRUPT', confident: true, reason: 'question detected' };

  const stripped = t.replace(/\b(oh|ah|um|uh|hmm|mm|yeah|yes|ok|okay|right|so|like|well)\b/g, ' ').replace(/\s+/g, ' ').trim();
  if (BACKCHANNELS.has(t) || (stripped === '' && words.length <= 4)) {
    if (assistantAskedQuestion && /^(yes|yeah|yep|yup|no|nope|sure|okay|ok|right|exactly|true)\b/.test(t)) {
      return { outcome: 'ANSWER', confident: true, reason: 'short reply to an assistant question' };
    }
    return { outcome: 'BACKCHANNEL', confident: true, reason: 'backchannel lexicon' };
  }
  if (NEGATION.test(t) && words.length <= 3 && !assistantAskedQuestion) return { outcome: 'INTERRUPT', confident: true, reason: 'short negation' };
  if (words.length >= 9) return { outcome: 'INTERRUPT', confident: true, reason: 'long utterance (>= 9 words)' };
  return { outcome: 'UNCLEAR', confident: false, reason: `${words.length} words, no lexical match` };
}

export function endsWithQuestion(text: string): boolean {
  return /\?\s*["')]*\s*$/.test(text.trim());
}
